import { createSuspenseQuery } from "../../utils/queryFactories.js";
import { z } from "zod";
import { api } from "../../utils/fetch.js";

const RequestSchema = z.object({
  id: z.string().describe("The board id of the resource")
});

const ResponseSchema = z.array(
  z
    .object({
      id: z.number(),
      value: z.string().optional(),
      description: z.string().optional()
    })
    .refine(
      (data) => data.value !== undefined || data.description !== undefined,
      {
        error: "Either value or description must be provided"
      }
    )
);

type Request = z.infer<typeof RequestSchema>;
type Response = z.infer<typeof ResponseSchema>;

const apis = {
  boardResourceStatuses: "resource-statuses",
  boardResourceTags: "resource-tags",
  boardResourceTypes: "resource-types",
  boardResourceCategories: "resource-categories",
  boardResourceIcons: "resource-icons"
} as const;

const boardValues = (boardApi: keyof typeof apis) =>
  createSuspenseQuery<Response, Request, Error>({
    queryKey: [apis[boardApi]],
    staleTime: 1000 * 60,
    fetcher: async (body) => {
      // await new Promise((resolve) => setTimeout(resolve, 10000));
      const result = await api<Request, Response>({
        method: "GET",
        url: `/board/:id/${apis[boardApi]}`,
        requestSchema: RequestSchema,
        responseSchema: ResponseSchema,
        body
      });
      return result.map((item) => ({
        ...item,
        value: item.value || item.description
      }));
    }
  });

type UseBoardValuesProps = {
  route: keyof typeof apis;
  id: string;
  valueIds?: string | number | number[] | string[] | null;
};

export const useBoardValues = (props: UseBoardValuesProps) => {
  const { route, id, valueIds } = props;
  const { data } = boardValues(route)({ variables: { id } });

  if (!valueIds) {
    return { data: [], values: [] };
  }

  let valueLabels: string[] = [];
  if (Array.isArray(valueIds)) {
    const stringValues = valueIds.map((item) => item.toString());
    valueLabels =
      data
        ?.filter((item) => stringValues.includes(item.id.toString()))
        .map((item) => item.value || item.description || "") ?? [];
  } else {
    valueLabels =
      data
        ?.filter((item) => item.id === Number(valueIds))
        .map((item) => item.value || item.description || "") ?? [];
  }

  return { data, values: valueLabels };
};
